import { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import DataTable from "react-data-table-component";
import { userRequest } from "../utils/requestMethods";
import { BackButton, CustomPagination, Header } from "../components";
import { useStateContext } from "../contexts/ContextProvider";
import { formatCurrency, getPriceForTerm } from "../utils/currencyUtils";
import { displaySchoolTerm } from "../utils/schoolYearUtils";
import {
  FiUser,
  FiUsers,
  FiBook,
  FiCalendar,
  FiCheckCircle,
  FiAlertCircle,
  FiTag,
  FiDownload,
  FiEdit2,
  FiCreditCard,
} from "react-icons/fi";
import SkeletonTable from "../components/common/SkeletonTable";
import { generateInvoicePDF } from "../utils/generateInvoicePDF";
import EditInvoiceModal from "../components/students/EditInvoiceModal";
import RecordOfflinePaymentModal from "../components/students/RecordOfflinePaymentModal";

const customStyles = {
  headRow: {
    style: { backgroundColor: "rgb(248, 250, 252)", fontWeight: "bold" },
  },
};

const statusStyles = {
  successful: { bg: "bg-green-100", text: "text-green-700", label: "Successful" },
  failed: { bg: "bg-red-100", text: "text-red-700", label: "Failed" },
  pending: { bg: "bg-yellow-100", text: "text-yellow-700", label: "Pending" },
};

const InfoRow = ({ label, value }) => (
  <div className="flex flex-col gap-0.5 py-3 border-b border-gray-100 last:border-0">
    <p className="text-xs text-gray-400">{label}</p>
    <p className="text-sm font-semibold text-gray-800">{value ?? "—"}</p>
  </div>
);

const FeeLineItem = ({
  label,
  description,
  amount,
  amountPaid,
  amountClassName = "text-gray-800",
}) => {
  const total = Number(amount) || 0;
  const paid = Number(amountPaid) || 0;

  return (
    <div className="flex items-start justify-between gap-3 py-2.5 border-b border-gray-100 text-sm">
      <div className="min-w-0">
        <span className="text-gray-600">{label}</span>
        {description && (
          <span className="block text-xs text-gray-400 font-normal">{description}</span>
        )}
      </div>
      <div className="text-right shrink-0">
        <span className={`font-medium ${amountClassName}`}>{formatCurrency(total)}</span>
        <span className="block text-[11px] text-gray-400 font-normal mt-0.5">
          Paid {formatCurrency(paid)}
        </span>
      </div>
    </div>
  );
};

const PaymentDetails = ({ history }) => {
  const { school } = useStateContext();
  const schoolId = school?._id;
  const location = useLocation();
  const paymentId = location.pathname.split("/")[3];
  const [payment, setPayment] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [studentAccount, setStudentAccount] = useState(null);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isOfflinePaymentOpen, setIsOfflinePaymentOpen] = useState(false);

  useEffect(() => {
    if (!paymentId || !schoolId) return;
    const fetch = async () => {
      try {
        setLoading(true);
        const { data } = await userRequest.get(
          `/transactions/payment/${schoolId}/${paymentId}?limit=100&sort_by=date&order=des`,
        );
        setTransactions(data.transactions || []);
        setPayment(data.payment);
        setStudentAccount(data.payment?.child?.account || null);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [paymentId, schoolId]);

  const handleDownloadInvoice = async () => {
    setExportingPDF(true);
    try {
      await generateInvoicePDF({ school, payment, studentAccount, totalPaid });
    } finally {
      setExportingPDF(false);
    }
  };

  const CHANNEL_LABELS = {
    cash: "Cash",
    bank_transfer: "Bank Transfer",
    cheque: "Cheque",
    pos: "POS",
  };

  const columns = [
    {
      name: "Amount Paid",
      cell: (row) => (
        <div className="flex flex-col gap-0.5 py-1">
          <span className="font-semibold text-green-700">{formatCurrency(row?.amount)}</span>
          {row.channel && (
            <span className="text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full w-fit">
              {CHANNEL_LABELS[row.channel] || row.channel}
            </span>
          )}
        </div>
      ),
    },
    {
      name: "Balance After",
      cell: (row) => formatCurrency(Number(row.total_amount) - Number(row.amount_after)),
    },
    {
      name: "Status",
      cell: (row) => {
        const s = statusStyles[row?.status] || statusStyles.pending;
        return (
          <span
            className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${s.bg} ${s.text}`}
          >
            {s.label}
          </span>
        );
      },
    },
    {
      name: "Reference",
      cell: (row) => {
        const isManual = row.reference?.startsWith("MAN-");
        return (
          <span className={`text-xs font-mono ${isManual ? "text-amber-700" : "text-gray-500"}`}>
            {isManual ? `Manual/${row.reference}` : row.reference || "—"}
          </span>
        );
      },
      grow: 1.5,
    },
    {
      name: "Date",
      cell: (row) => new Date(row.updatedAt).toLocaleString("en-NG", { hour12: true }),
      sortable: true,
    },
  ];

  const conditionalRowStyles = [
    {
      when: (row) => Boolean(row.channel),
      style: { backgroundColor: "rgb(255 251 235)", borderLeft: "3px solid rgb(245 158 11)" },
    },
  ];

  const totalPaid = transactions.reduce((sum, t) => sum + Number(t?.amount || 0), 0);
  const outstanding =
    Number(payment?.total_amount || 0) - Number(payment?.discount?.amount || 0) - totalPaid;

  // The backend now computes these directly on the payment (accounting for any
  // unpaid balance carried over from a previous term); fall back to the local
  // calculation above for older responses that don't include them yet.
  const currentRemaining = payment?.current_remaining ?? Math.max(0, outstanding);
  const outstandingPayment = payment?.outstanding_payment || null;
  const outstandingRemaining =
    payment?.outstanding_remaining ??
    (outstandingPayment
      ? Math.max(
          0,
          Number(outstandingPayment.total_amount || 0) -
            Number(outstandingPayment.amount_paid || 0),
        )
      : 0);
  const totalDue = payment?.total_due ?? currentRemaining + outstandingRemaining;
  const isFullySettled = payment?.is_fully_settled ?? totalDue <= 0;

  // Receipt breakdown for this invoice. `subtotal` is the gross of tuition +
  // extra fees + arrears (payment.total_amount already equals that sum).
  const subtotal = Number(payment?.total_amount) || 0;
  const discountAmt = Number(payment?.discount?.amount) || 0;
  const invoicedTotal = Math.max(0, subtotal - discountAmt);
  // The backend folds a granted discount into `payment.amount_paid` (it counts
  // as settled the moment it's given), so that field is cash + discount
  // combined. `totalPaid` above, summed straight from the transaction/payment
  // history records, is real cash only — use that for "Amount Paid" so it
  // never repeats the same figure the Discount card already shows.
  const cashReceived = totalPaid;
  const balanceDueLabel = outstandingRemaining > 0 ? "Balance Due (this term)" : "Balance Due";

  const studentName = payment?.child
    ? `${payment.child.firstname} ${payment.child.lastname}`
    : "Student Information";

  return (
    <div className="m-4 md:m-10 mt-24 p-6 md:p-10 bg-white dark:bg-secondary-dark-bg rounded-3xl">
      <div className="flex items-center justify-between mb-2">
        <BackButton history={history} />
        {payment && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsOfflinePaymentOpen(true)}
              className="inline-flex items-center gap-2 py-2 px-4 text-sm font-medium rounded-full text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 transition-colors"
            >
              <FiDownload className="w-4 h-4 rotate-180" />
              Record Payment
            </button>
            <button
              onClick={() => setIsEditModalOpen(true)}
              className="inline-flex items-center gap-2 py-2 px-4 text-sm font-medium rounded-full text-indigo-600 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 transition-colors"
            >
              <FiEdit2 className="w-4 h-4" />
              Edit Invoice
            </button>
            <button
              onClick={handleDownloadInvoice}
              disabled={exportingPDF}
              className="inline-flex items-center gap-2 py-2 px-4 text-sm font-medium rounded-full text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 transition-colors"
            >
              <FiDownload className="w-4 h-4" />
              {exportingPDF ? "Generating..." : "Download Invoice"}
            </button>
          </div>
        )}
      </div>
      <Header category="Payment" title={studentName} />

      {loading ? (
        <SkeletonTable rows={6} columns={2} />
      ) : (
        <div className="space-y-5 max-w-5xl mx-auto">
          {/* Status + balance banner */}
          <div
            className={`flex items-center gap-3 rounded-2xl px-5 py-4 border
            ${isFullySettled ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"}`}
          >
            {isFullySettled ? (
              <FiCheckCircle className="text-green-600 text-2xl shrink-0" />
            ) : (
              <FiAlertCircle className="text-amber-500 text-2xl shrink-0" />
            )}
            <div>
              <p
                className={`font-semibold text-sm ${isFullySettled ? "text-green-700" : "text-amber-700"}`}
              >
                {isFullySettled ? "Fully Paid" : "Total Bill"}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {displaySchoolTerm(payment?.term)} · {payment?.session}
              </p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-xs text-gray-400">
                {outstandingRemaining > 0 ? "Total Due" : "Outstanding"}
              </p>
              <p
                className={`text-xl font-bold ${isFullySettled ? "text-green-600" : "text-red-500"}`}
              >
                {formatCurrency(Math.max(0, totalDue))}
              </p>
              {outstandingRemaining > 0 && (
                <p className="text-xs text-gray-400 mt-0.5">
                  {formatCurrency(currentRemaining)} this term +{" "}
                  {formatCurrency(outstandingRemaining)} carried over
                </p>
              )}
            </div>
          </div>

          {/* Outstanding balance carried over from a previous term */}
          {outstandingPayment && outstandingRemaining > 0 && (
            <div className="flex items-start gap-3 rounded-2xl px-5 py-4 border bg-red-50 border-red-200">
              <FiAlertCircle className="text-red-500 text-2xl shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-sm text-red-700">
                  Outstanding Balance from Previous Term
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {displaySchoolTerm(outstandingPayment.term)} · {outstandingPayment.session}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {formatCurrency(outstandingPayment.total_amount)} billed ·{" "}
                  {formatCurrency(outstandingPayment.amount_paid)} paid
                </p>
                {outstandingPayment._id && (
                  <Link
                    to={`/class-transaction/payment/${outstandingPayment._id}/${payment?.child?._id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 hover:text-red-800 underline mt-2"
                  >
                    View Previous Invoice →
                  </Link>
                )}
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-400">Balance</p>
                <p className="text-xl font-bold text-red-600">
                  {formatCurrency(outstandingRemaining)}
                </p>
              </div>
            </div>
          )}

          {/* Amount summary row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-4">
              <p className="text-xs text-gray-400 mb-1">Total Fee</p>
              <p className="text-base font-bold text-gray-800">{formatCurrency(subtotal)}</p>
            </div>
            <div className="rounded-2xl border border-green-100 bg-green-50 px-4 py-4">
              <p className="text-xs text-gray-400 mb-1">Amount Paid</p>
              <p className="text-base font-bold text-green-700">{formatCurrency(cashReceived)}</p>
              {discountAmt > 0 && (
                <p className="text-[11px] text-gray-400 mt-0.5">Cash received, excludes discount</p>
              )}
            </div>
            {discountAmt > 0 && (
              <div className="rounded-2xl border border-indigo-100 bg-indigo-50 px-4 py-4">
                <p className="text-xs text-gray-400 mb-1">Discount Given</p>
                <p className="text-base font-bold text-indigo-700">{formatCurrency(discountAmt)}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">Waived, not cash collected</p>
              </div>
            )}
            <div
              className={`rounded-2xl border px-4 py-4 ${currentRemaining > 0 ? "border-red-100 bg-red-50" : "border-green-100 bg-green-50"}`}
            >
              <p className="text-xs text-gray-400 mb-1">{balanceDueLabel}</p>
              <p
                className={`text-base font-bold ${currentRemaining > 0 ? "text-red-600" : "text-green-700"}`}
              >
                {formatCurrency(currentRemaining)}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Left — student info card */}
            <div className="rounded-2xl border border-gray-200 px-5 py-5 space-y-1">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                Details
              </p>

              <InfoRow
                label={
                  <span className="flex items-center gap-1">
                    <FiUser className="text-gray-400 text-xs" /> Student
                  </span>
                }
                value={`${payment?.child?.firstname || ""} ${payment?.child?.lastname || ""}`}
              />
              <InfoRow
                label={
                  <span className="flex items-center gap-1">
                    <FiUsers className="text-gray-400 text-xs" /> Parent
                  </span>
                }
                value={
                  <span>
                    <span className="block">
                      {payment?.user?.firstname} {payment?.user?.lastname}
                    </span>
                    <span className="text-xs text-gray-400 font-normal">
                      {payment?.user?.email}
                    </span>
                  </span>
                }
              />
              <InfoRow
                label={
                  <span className="flex items-center gap-1">
                    <FiBook className="text-gray-400 text-xs" /> Class
                  </span>
                }
                value={payment?.class?.class_name}
              />
              <InfoRow
                label={
                  <span className="flex items-center gap-1">
                    <FiCalendar className="text-gray-400 text-xs" /> Term / Session
                  </span>
                }
                value={`${displaySchoolTerm(payment?.term)} · ${payment?.session}`}
              />
              {payment?.child?.account && (
                <InfoRow
                  label={
                    <span className="flex items-center gap-1">
                      <FiCreditCard className="text-gray-400 text-xs" /> Virtual Account
                    </span>
                  }
                  value={
                    <span>
                      <span className="block font-mono">{payment.child.account.accountNumber}</span>
                      <span className="text-xs text-gray-400 font-normal">
                        {payment.child.account.bankName} · {payment.child.account.accountName}
                      </span>
                    </span>
                  }
                />
              )}

              {/* Fee breakdown */}
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider pt-3 pb-1">
                Fee Breakdown
              </p>
              <div className="flex justify-between py-2 border-b border-gray-100 text-sm">
                <span className="text-gray-600">School Fees</span>
                <span className="font-medium text-gray-800">
                  {typeof payment?.class_price === "object"
                    ? formatCurrency(getPriceForTerm(payment.class_price, String(payment.term)))
                    : formatCurrency(payment?.class_price)}
                </span>
              </div>

              {/* Extra fees */}
              {(payment?.add_ons?.length > 0 || payment?.transport_add_on?.add_on_name) && (
                <>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider pt-3 pb-1">
                    <FiTag className="inline mr-1" />
                    Extra Fees
                  </p>
                  {payment.add_ons?.map((addon) => (
                    <FeeLineItem
                      key={addon._id || addon.add_on_name}
                      label={addon.add_on_name}
                      amount={addon.add_on_price}
                      amountPaid={addon.amount_paid}
                    />
                  ))}
                  {payment.transport_add_on?.add_on_name && (
                    <FeeLineItem
                      label={`Bus: ${payment.transport_add_on.add_on_name}`}
                      amount={payment.transport_add_on.add_on_price}
                      amountPaid={payment.transport_add_on.amount_paid}
                    />
                  )}
                </>
              )}

              {/* Arrears */}
              {payment?.arrears?.length > 0 && (
                <>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider pt-3 pb-1">
                    <FiAlertCircle className="inline mr-1" />
                    Arrears
                  </p>
                  {payment.arrears.map((arrear) => (
                    <FeeLineItem
                      key={arrear._id || arrear.name}
                      label={arrear.name}
                      description={arrear.description}
                      amount={arrear.amount}
                      amountPaid={arrear.amount_paid}
                      amountClassName="text-red-600"
                    />
                  ))}
                </>
              )}

              {discountAmt > 0 ? (
                <>
                  {/* Subtotal */}
                  <div className="flex justify-between py-2 border-t border-gray-200 mt-1 text-sm">
                    <span className="font-semibold text-gray-700">Subtotal</span>
                    <span className="font-semibold text-gray-800">{formatCurrency(subtotal)}</span>
                  </div>

                  {/* Discount — a highlighted row, not a card that reads like a payment */}
                  <div className="flex justify-between items-center py-2 px-2.5 -mx-2.5 my-1 bg-green-50 rounded-lg text-sm">
                    <span className="text-green-700 font-medium">Discount</span>
                    <span className="text-green-700 font-semibold">
                      −{formatCurrency(discountAmt)}
                    </span>
                  </div>

                  <div className="flex justify-between py-2 border-b border-gray-200 text-sm">
                    <span className="font-semibold text-gray-700">Invoiced Total</span>
                    <span className="font-bold text-gray-900">{formatCurrency(invoicedTotal)}</span>
                  </div>
                </>
              ) : (
                <div className="flex justify-between py-2 border-t border-gray-200 mt-1 text-sm">
                  <span className="font-semibold text-gray-700">Total Fee</span>
                  <span className="font-bold text-gray-900">{formatCurrency(subtotal)}</span>
                </div>
              )}

              {/* Amount paid — cash only, deliberately styled differently from the
                  discount highlight above so the two can't be mistaken for each other */}
              <div className="flex justify-between py-2 text-sm">
                <span className="text-gray-600">Amount Paid{discountAmt > 0 ? " (cash)" : ""}</span>
                <span className="font-medium text-green-700">−{formatCurrency(cashReceived)}</span>
              </div>

              <div className="flex justify-between py-2 border-b border-gray-200 text-sm">
                <span className="font-semibold text-gray-700">{balanceDueLabel}</span>
                <span
                  className={`font-bold ${currentRemaining > 0 ? "text-red-600" : "text-green-700"}`}
                >
                  {formatCurrency(currentRemaining)}
                </span>
              </div>

              {/* Previous term balance */}
              {outstandingPayment && outstandingRemaining > 0 && (
                <>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider pt-3 pb-1">
                    <FiAlertCircle className="inline mr-1" />
                    Previous Term Balance
                  </p>
                  <div className="flex justify-between py-2 border-b border-gray-100 text-sm">
                    <span className="text-gray-600">
                      {displaySchoolTerm(outstandingPayment.term)} · {outstandingPayment.session}
                      <span className="block text-xs text-gray-400 font-normal">
                        {formatCurrency(outstandingPayment.total_amount)} billed ·{" "}
                        {formatCurrency(outstandingPayment.amount_paid)} paid
                      </span>
                      {outstandingPayment._id && (
                        <Link
                          to={`/class-transaction/payment/${outstandingPayment._id}/${payment?.child?._id}`}
                          className="block text-xs font-medium text-indigo-600 hover:text-indigo-700 underline mt-0.5"
                        >
                          View Invoice
                        </Link>
                      )}
                    </span>
                    <span className="font-medium text-red-600">
                      {formatCurrency(outstandingRemaining)}
                    </span>
                  </div>

                  {/* Grand total — only shown when it differs from Balance Due above */}
                  <div className="flex justify-between pt-3 mt-1 border-t-2 border-gray-200 text-sm">
                    <span className="font-semibold text-gray-700">Total Due (all terms)</span>
                    <span className="font-bold text-gray-900">{formatCurrency(totalDue)}</span>
                  </div>
                </>
              )}
            </div>

            {/* Right — payment history table */}
            <div className="rounded-2xl border border-gray-200 px-5 py-5 col-span-1 lg:col-span-2">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
                Payment History
              </p>
              <DataTable
                data={transactions}
                columns={columns}
                getRowId={(row) => row?._id}
                pagination
                paginationComponent={CustomPagination}
                paginationRowsPerPageOptions={[10, 20, 30]}
                customStyles={customStyles}
                conditionalRowStyles={conditionalRowStyles}
                noDataComponent={
                  <div className="py-10 text-center text-sm text-gray-400">
                    No payment history found.
                  </div>
                }
                highlightOnHover
              />
            </div>
          </div>
        </div>
      )}

      {isOfflinePaymentOpen && (
        <RecordOfflinePaymentModal
          payment={payment}
          closeModal={() => setIsOfflinePaymentOpen(false)}
          onSuccess={() => {
            setLoading(true);
            userRequest
              .get(
                `/transactions/payment/${schoolId}/${paymentId}?limit=100&sort_by=date&order=des`,
              )
              .then(({ data }) => {
                setTransactions(data.transactions || []);
                setPayment(data.payment);
                setStudentAccount(data.payment?.child?.account || null);
              })
              .catch(console.log)
              .finally(() => setLoading(false));
          }}
        />
      )}

      {isEditModalOpen && (
        <EditInvoiceModal
          payment={payment}
          schoolId={schoolId}
          schoolCode={school?.code}
          closeModal={() => setIsEditModalOpen(false)}
          onSuccess={() => {
            setLoading(true);
            userRequest
              .get(
                `/transactions/payment/${schoolId}/${paymentId}?limit=100&sort_by=date&order=des`,
              )
              .then(({ data }) => {
                setTransactions(data.transactions || []);
                setPayment(data.payment);
              })
              .catch(console.log)
              .finally(() => setLoading(false));
          }}
        />
      )}
    </div>
  );
};

export default PaymentDetails;
